import { useEffect } from "react";

import { css } from "@compiled/react";
import { defineMessages, FormattedMessage } from "react-intl";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";

import {
    ufoEventRedirectToProduct,
    UfoCustomTimingKey,
    UfoCustomTimingWrapper
} from "@/components/signup/utils";
import MainContainer from "@/templates/ondemand/signup/confirmation/confirmation-page/src/components/main-container/main-container";

import { useAnalyticsClient } from "../../../../../../../../analytics/wac-analytics/analytics-client";
import { DwellTimeWrapper } from "../../components/dwell-time-wrapper";
import { MessageContainerProps } from "../../components/styles";
import { useSearchParameters } from "../../hooks";
import { ProductKey } from "../../models";
import ProgressBar from "../../sections/progress-bar";

import { StyledRelativePosition } from "./robots-view.styles";

const MessageContainer = css(MessageContainerProps);

let image =
    "https://wac-cdn.atlassian.com/misc-assets/webp-images/construction.webp";

const RobotsView = /* istanbul ignore next */ () => {
    const analyticsClient = useAnalyticsClient();
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const { products, referrer } = useSearchParameters();

    const isReferredFromVerifyEmail = referrer === "verify_email";

    const isBitbucket =
        products.length === 1 && products[0] === ProductKey.BITBUCKET;

    // Check if "loom" is in the products array
    if (products.includes(ProductKey.LOOM)) {
        image =
            "https://dam-cdn.atl.orangelogic.com/AssetLink/50qw66f865jv256a1c6365h05ncwhx54.svg";
    }

    useEffect(() => {
        analyticsClient &&
            createAnalyticsEvent({
                eventName: "robotsPageViewed",
                eventComponent: "robotsPage",
                event: "viewed"
            }).fire("signup");
    }, [analyticsClient, createAnalyticsEvent]);

    return (
        <UfoCustomTimingWrapper
            ufoEvent={ufoEventRedirectToProduct}
            ufoCustomTimingKey={UfoCustomTimingKey.ROBOTS_VIEW_DWELL_TIME}
        >
            <DwellTimeWrapper
                eventData={{
                    eventName: "robotsPageDwelled",
                    eventComponent: "robotsPage",
                    event: "dwelled",
                    actionSubject: "robotsPage",
                    source: "bxp-web-platform-wac-confirmation-page"
                }}
                initialPollInterval={2000}
                idleTimeout={120000}
                nextPollIntervalCallback={(x) => x + 2000}
            >
                <StyledRelativePosition>
                    <MainContainer>
                        {/* eslint-disable-next-line no-restricted-syntax */}
                        <img src={image} height="300px" alt="" />
                        <ProgressBar />

                        <div
                            css={MessageContainer}
                            data-testid="robots-message"
                        >
                            <FormattedMessage
                                {...(isBitbucket
                                    ? RobotsView.messages.robotsBitbucket
                                    : RobotsView.messages.robots)}
                                values={{
                                    isReferredFromVerifyEmail,
                                    p: (chunks) => <p>{chunks}</p>,
                                    br: <br />
                                }}
                            />
                        </div>
                    </MainContainer>
                </StyledRelativePosition>
            </DwellTimeWrapper>
        </UfoCustomTimingWrapper>
    );
};

export default RobotsView;

RobotsView.messages = defineMessages({
    robots: {
        id: "WacConfirmationPage.robots",
        defaultMessage: `<p>{ isReferredFromVerifyEmail, select, true {Great, your email address is now verified! Our robots are working on your Atlassian Cloud site.} other {Thanks for signing up! Our robots are working on your Atlassian Cloud site.} }{br}\nThis won't take more than a minute or two. You'll be taken there once it's ready.<\/p>`
    },
    robotsBitbucket: {
        id: "WacConfirmationPage.robotsBitbucket",
        defaultMessage: `<p>{ isReferredFromVerifyEmail, select, true {Great, your email address is now verified! Our robots are working on your Atlassian Cloud product.} other {Thanks for signing up! Our robots are working on your Atlassian Cloud product.} }{br}\nThis won't take more than a minute or two. You'll be taken there once it's ready.<\/p>`
    }
});
